import { useEffect, useCallback } from 'react';
import { Platform } from 'react-native';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useMessageList } from '../Context/MessageListContext';
import { getUserChats } from '@api/api';

export const useChatHistory = () => {
	const {
		state: { chatHistory },
		dispatch: contextDispatch,
	} = useMessageList();
	const helpModalDisplayed = useSelector(
		(state: DefaultRootState) => state.modals.helpModal.modalDisplayed
	);
	const easyMode = useSelector(
		(state: DefaultRootState) => state.UI.easyMode
	);
	const currentUser = useSelector(
		(state: DefaultRootState) => state.promiseData.data.userData
	);
	const userId = currentUser?._id || currentUser?.id || currentUser?.userId;

	const fetchChatHistory = useCallback(async () => {
		if (!userId) {
			return;
		}

		try {
			contextDispatch({
				type: 'SET_LOADING_CHAT_HISTORY',
				payload: true,
			});
			console.log(
				'💬 useChatHistory - Fetching chat history for user:',
				userId
			);

			const response = await getUserChats(userId, {
				page: 1,
				limit: 50,
				sortBy: 'lastMessageAt',
				sortOrder: 'desc',
			});

			const chats = response?.data?.chats || response?.chats || [];
			console.log('💬 useChatHistory - Fetched chats:', chats.length);
			contextDispatch({ type: 'SET_CHAT_HISTORY', payload: chats });
		} catch (error) {
			console.error(
				'❌ useChatHistory - Error fetching chat history:',
				error
			);
			contextDispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
		} finally {
			contextDispatch({
				type: 'SET_LOADING_CHAT_HISTORY',
				payload: false,
			});
		}
	}, [userId, contextDispatch]);

	// Fetch chat history on mount when userId is available
	useEffect(() => {
		if (userId) {
			console.log(
				'💬 useChatHistory - Component mounted, fetching chat history'
			);
			fetchChatHistory();
		}
	}, [userId, fetchChatHistory]); // Fetch on mount and when userId changes

	// Refresh chat history when help modal opens or easy mode is enabled
	// This ensures fresh data when the chat history component is displayed
	useEffect(() => {
		const isChatHistoryVisible = helpModalDisplayed || easyMode;

		console.log('💬 useChatHistory useEffect:', {
			helpModalDisplayed,
			easyMode,
			isChatHistoryVisible,
			userId,
		});

		if (isChatHistoryVisible && userId) {
			console.log(
				'💬 useChatHistory - Chat history component visible, refreshing'
			);
			fetchChatHistory();
		}
	}, [helpModalDisplayed, easyMode, userId, fetchChatHistory]);

	// Listen for chat saved events to refresh history (web only)
	useEffect(() => {
		// Only use window events on web platform
		if (Platform.OS !== 'web' || typeof window === 'undefined') {
			return;
		}

		const handleChatSaved = () => {
			console.log(
				'💬 useChatHistory - Chat saved event received, refreshing history'
			);
			if (userId) {
				fetchChatHistory();
			}
		};

		if (window.addEventListener) {
			window.addEventListener('chatSaved', handleChatSaved);
		}

		return () => {
			if (typeof window !== 'undefined' && window.removeEventListener) {
				window.removeEventListener('chatSaved', handleChatSaved);
			}
		};
	}, [userId, fetchChatHistory]);

	return { fetchChatHistory };
};

